"use client";

import { FILTER_MENU_Z_INDEX } from "@faire/slate/private/filter/consts";
import { FilterMenuBase } from "@faire/slate/private/filter/FilterMenuBase";
import { useFilterMenuContext } from "@faire/slate/private/filter/FilterMenuContext";
import { FilterMenuFooterAction } from "@faire/slate/private/filter/FilterMenuFooterAction";
import { FilterMenuHeader } from "@faire/slate/private/filter/FilterMenuHeader";
import type { IFilterMenuTitleTriggerProps } from "@faire/slate/private/filter/types";
import type { IActionProps } from "@faire/slate/private/shared/types";
import React from "react";

type IProps = {
  /**
   * Filter options shown inside the popup body.
   */
  children: React.ReactNode;

  /**
   * Optional label for the popup header's Clear button. When provided, a
   * Clear button renders in the header that fires the parent `FilterMenu`'s
   * `onClear` and closes the popup.
   */
  clearActionLabel?: string;

  /**
   * Props for the footer action (typically "Apply"). The rendered `<Button>`
   * is locked to `size="small"` + `fullWidth`. Omit to suppress the footer.
   */
  footerActionProps?: IActionProps;

  /**
   * The popup title. Also used as the popup's accessible name (`aria-label`).
   */
  title: string;

  /**
   * Optional title trigger rendered after the title. Appearance is fixed
   * (tertiary information icon button, 16px). Supply `aria-label` and
   * `onClick`.
   */
  titleTriggerProps?: IFilterMenuTitleTriggerProps;

  /**
   * **web**
   * The menu body popup's z-index.
   *
   * @default 5
   */
  zIndex?: number;

  // DO NOT ADD any new prop without aligning with Slate team first
};

/**
 * Renders the contents of a `FilterMenu`'s popup. Must be a child of
 * `FilterMenu`.
 *
 * ```tsx
 * <FilterMenu
 *   applied={!!selected}
 *   label="Size"
 *   onClear={() => setSelected(undefined)}
 * >
 *   <FilterMenuBody
 *     title="Size"
 *     clearActionLabel="Clear"
 *     footerActionProps={{ label: "Apply", onClick: handleApply }}
 *   >
 *     <Radio label="Small" name="size" />
 *     <Radio label="Large" name="size" />
 *   </FilterMenuBody>
 * </FilterMenu>
 * ```
 */
export const FilterMenuBody: React.FC<IProps> = ({
  children,
  clearActionLabel,
  footerActionProps,
  title,
  titleTriggerProps,
  zIndex = FILTER_MENU_Z_INDEX,
}) => {
  const { id } = useFilterMenuContext();

  return (
    <FilterMenuBase
      data-component-name="Slate/FilterMenuBody"
      id={id}
      title={title}
      zIndex={zIndex}
    >
      <FilterMenuHeader
        clearActionLabel={clearActionLabel}
        title={title}
        titleTriggerProps={titleTriggerProps}
      />
      <div className="fs-filter-menu__content">{children}</div>
      {footerActionProps ? (
        <FilterMenuFooterAction {...footerActionProps} />
      ) : null}
    </FilterMenuBase>
  );
};
